// src/backendServices/documentManagment/api_document.ts

import axios from 'axios';
import { API_URLS, WWW_URLS } from '../urls';
import { BackendResponse, handleError } from '../../dataTypes/backendServicesManagment/types';
import { Document } from '../../dataTypes/documentManagment/types';
import { createUpload } from '../fileManagment/uploadWithStore';

export interface BackendResponse_Document extends BackendResponse<Document> {}
export interface BackendResponse_DocumentList extends BackendResponse<Document[]> {}

/**
 * Récupère la liste des documents associés à une entité
 */
export const getDocumentsByEntityId = async (
  entityType: string,
  entityId: number
): Promise<BackendResponse_DocumentList> => {
  const url = `${API_URLS.getDocuments}?entityType=${entityType}&entityId=${entityId}`;

  try {
    const response = await axios.get(url);

    if (response.status === 200) {
      // Collection GetCollection() par défaut d'ApiPlatform (pas de contrôleur custom pour
      // Document, contrairement à ImageList/SoundList) : réponse au format Hydra/JSON-LD,
      // les éléments sont sous 'hydra:member', pas un tableau brut à la racine.
      const rawDocuments = response.data['hydra:member'] ?? response.data;

      const documents: Document[] = rawDocuments.map((doc: any) => ({
        id: doc.id,
        name: doc.name,
        fileName: doc.fileName,
        originalFile: doc.originalFile ?? '',
        fileType: doc.fileType,
        creationDate: doc.creationDate ? new Date(doc.creationDate.date || doc.creationDate) : new Date(),
        creationIpAddress: doc.creationIpAddress ?? undefined,
        fileToUpload: null,
        isPublic: doc.isPublic ?? false,
        uuid: doc.uuid ?? undefined,
        publicRef: doc.publicRef ?? undefined,
      }));

      return {
        code: 1,
        comment: 'Documents retrieved successfully.',
        payload: documents
      };
    } else {
      return handleError<Document[]>(response.status, 'Non-200 response from API');
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<Document[]>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<Document[]>(-102, 'Unknown error');
  }
};

/**
 * Récupère un document spécifique par son ID
 */
export const getDocumentById = async (documentId: number): Promise<BackendResponse_Document> => {
  const url = API_URLS.getDocument.replace('{id}', `${documentId}`);

  try {
    const response = await axios.get(url);

    if (response.status === 200) {
      const doc = response.data;
      const document: Document = {
        id: doc.id,
        name: doc.name,
        fileName: doc.fileName,
        originalFile: doc.originalFile ?? '',
        fileType: doc.fileType,
        creationDate: doc.creationDate ? new Date(doc.creationDate.date || doc.creationDate) : new Date(),
        creationIpAddress: doc.creationIpAddress ?? undefined,
        fileToUpload: null,
        isPublic: doc.isPublic ?? false,
        uuid: doc.uuid ?? undefined,
        publicRef: doc.publicRef ?? undefined,
      };

      return {
        code: 1,
        comment: 'Document retrieved successfully.',
        payload: document
      };
    } else {
      return handleError<Document>(response.status, 'Non-200 response from API');
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<Document>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<Document>(-102, 'Unknown error');
  }
};

/**
 * Crée les métadonnées d'un document (première étape non-bloquante)
 */
export const createDocumentMetadata = async (
  name: string,
  fileName: string,
  fileType: string,
  fileSize: number,
  entityType: string,
  entityId: number,
  adminId?: number,
  isPublic?: boolean
): Promise<BackendResponse> => {
  const url = API_URLS.createDocument;

  const data = {
    name,
    fileName,
    fileType,
    fileSize,
    entityType,
    entityId,
    uploadStatus: 'pending',
    adminId,
    isPublic: isPublic ?? false
  };

  try {
    const response = await axios.post(url, data, {
      headers: { Accept: 'application/ld+json' }
    });

    if (response.status === 201) {
      const documentId = response.data.id || response.data.idBis;
      return {
        code: 1,
        comment: 'Document metadata created successfully.',
        payload: documentId
      };
    } else {
      return handleError<void>(response.status, 'Error creating document metadata');
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<void>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<void>(-102, 'Unknown error');
  }
};

/**
 * Crée un document avec upload en arrière-plan (non-bloquant)
 */
export const createDocument = async (
  file: File,
  name: string,
  entityType: string,
  entityId: number,
  setProgress?: ((progress: number) => void) | null,
  adminId?: number,
  isPublic?: boolean
): Promise<BackendResponse> => {

console.log ('	createDocument : ', name, entityType, entityId)

  try {
    // 1. Créer d'abord les métadonnées du document
    const metadataResult = await createDocumentMetadata(
      name,
      file.name,
      file.type || 'application/octet-stream',
      file.size,
      entityType,
      entityId,
      adminId,
      isPublic
    );

    if (metadataResult.code !== 1) {
      return metadataResult;
    }

    const documentId = metadataResult.payload;

    // 2. Lancer l'upload du fichier en arrière-plan
    const uploadConfig = {
      uploadChunkUrl: API_URLS.uploadDocumentChunk,
      finalizeUploadUrl: API_URLS.finalizeDocumentUpload,
      chunkSize: 5 * 1024 * 1024, // 5MB pour les documents
      maxRetries: 3,
      enableResume: true,
      entityId: documentId,
      entityType: 'document',
      autoStart: true,
      metadata: {
        documentId: documentId,
        name: name,
        entityType: entityType,
        entityId: entityId,
        fileName: file.name
      }
    };

    const uploader = createUpload(file, uploadConfig);

    console.log(`Upload document démarré en arrière-plan pour le document ID: ${documentId}, Upload ID: ${uploader.id}`);

    // 3. Retourner immédiatement le résultat (non-bloquant)
    return {
      code: 1,
      comment: 'Document created successfully. Upload started in background.',
      payload: documentId
    };

  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<void>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<void>(-102, 'Unknown error creating document');
  }
};

/**
 * Crée plusieurs documents en lot
 */
export const createDocuments = async (
  files: File[],
  documents: Document[],
  entityType: string,
  entityId: number,
  setProgress?: ((progress: number) => void) | null,
  adminId?: number
): Promise<BackendResponse> => {
  try {
    const results: number[] = [];
    const errors: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const doc = documents[i];

      const result = await createDocument(
        file,
        doc.name,
        entityType,
        entityId,
        setProgress,
        adminId,
        doc.isPublic
      );

      if (result.code === 1) {
        results.push(result.payload);
      } else {
        errors.push(`Failed to create document: ${doc.name}`);
      }
    }

    if (results.length > 0) {
      return {
        code: 1,
        comment: errors.length > 0 
          ? `Created ${results.length} documents. ${errors.length} failed.`
          : 'All documents created successfully.',
        payload: results
      };
    } else {
      return handleError<void>(-1, `Failed to create any documents. Errors: ${errors.join(', ')}`);
    }

  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<void>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<void>(-102, 'Unknown error creating documents');
  }
};

/**
 * Met à jour un document existant (métadonnées et/ou fichier)
 */
export const updateDocument = async (
  documentId: number,
  name: string,
  file?: File | null,
  setProgress?: ((progress: number) => void) | null
): Promise<BackendResponse> => {
  const url = API_URLS.updateDocument;

  const formData = new FormData();
  formData.append('id', documentId.toString());
  formData.append('name', name);

  if (file) {
    formData.append('file', file);
    formData.append('fileName', file.name);
    formData.append('fileType', file.type || 'application/octet-stream');
    formData.append('fileSize', file.size.toString());
  }

  const headers = {
    Accept: 'application/ld+json'
  };

  try {
    // 1. Mettre à jour les métadonnées
    const response = await axios.post(url, formData, { headers });

    if (response.status === 200 || response.status === 201) {
      
      // 2. Si un nouveau fichier est fourni, lancer l'upload en arrière-plan
      if (file) {
        const uploadConfig = {
          uploadChunkUrl: API_URLS.uploadDocumentChunk,
          finalizeUploadUrl: API_URLS.finalizeDocumentUpload,
          chunkSize: 5 * 1024 * 1024,
          maxRetries: 3,
          enableResume: true,
          entityId: documentId,
          entityType: 'document',
          autoStart: true,
          metadata: {
            documentId: documentId,
            name: name,
            fileName: file.name,
            isUpdate: true
          }
        };

        const uploader = createUpload(file, uploadConfig);
        console.log(`Upload document (mise à jour) démarré en arrière-plan pour le document ID: ${documentId}, Upload ID: ${uploader.id}`);
      }

      return {
        code: 1,
        comment: file ? 'Document updated successfully. Upload started in background.' : 'Document updated successfully.',
        payload: {} as any
      };
    } else {
      return handleError<void>(response.status, 'Non-200/201 response from API');
    }

  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<void>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<void>(-102, 'Unknown error');
  }
};

/**
 * Supprime un document
 */
export const deleteDocument = async (documentId: number): Promise<BackendResponse> => {
  const url = `${API_URLS.deleteDocument}/${documentId}`;

  const headers = {
    Accept: 'application/ld+json'
  };

  try {
    const response = await axios.delete(url, { headers });

    if (response.status === 204 || response.status === 200) {
      return {
        code: 1,
        comment: 'Document deleted successfully.',
        payload: {} as any
      };
    } else if (response.status === 409) {
      return {
        code: -103,
        comment: 'The document is linked with other entities.',
        payload: {} as any
      };
    } else {
      return handleError<void>(response.status, 'Error deleting the document');
    }

  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<void>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<void>(-102, 'Unknown error');
  }
};

/**
 * Supprime plusieurs documents
 */
export const deleteDocuments = async (documentIds: number[]): Promise<BackendResponse> => {
  const url = API_URLS.deleteDocuments;

  const data = {
    documentIds
  };

  try {
    const response = await axios.post(url, data, {
      headers: { Accept: 'application/ld+json' }
    });

    if (response.status === 200 || response.status === 204) {
      return {
        code: 1,
        comment: 'Documents deleted successfully.',
        payload: {} as any
      };
    } else {
      return handleError<void>(response.status, 'Error deleting documents');
    }

  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<void>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<void>(-102, 'Unknown error');
  }
};

/**
 * URL à utiliser pour afficher/télécharger le fichier d'un document : URL publique
 * (media server, par uuid) si le document est public, sinon endpoint privé authentifié.
 */
export const getDocumentUrl = (doc: Document): string => {
  if (doc.isPublic && doc.uuid && doc.fileName) {
    const ext = doc.fileName.includes('.') ? doc.fileName.split('.').pop() : 'bin';
    return `${WWW_URLS.MediaServer}${doc.publicRef}/documents/${doc.id}.${ext}`;
  }

  return API_URLS.getDocumentFile.replace('{id}', `${doc.id}`);
};

/**
 * Récupère l'URL de téléchargement pour un document
 */
export const getDocumentDownloadUrl = async (documentId: number): Promise<string> => {
  try {
    const response = await axios.get(`${API_URLS.getDocumentDownload}?id=${documentId}`);
    
    if (response.status === 200) {
      return response.data.url || response.data;
    } else {
      throw new Error('Failed to get download URL');
    }

  } catch (error) {
    console.error('Error getting document download URL:', error);
    throw error;
  }
};

/**
 * Marque un document comme complètement uploadé (appelé après finalisation de l'upload)
 */
export const markDocumentAsUploaded = async (documentId: number): Promise<BackendResponse> => {
  const url = `${API_URLS.markDocumentUploaded}`;

  const data = {
    documentId,
    uploadStatus: 'completed'
  };

  try {
    const response = await axios.post(url, data, {
      headers: { Accept: 'application/ld+json' }
    });

    if (response.status === 200 || response.status === 204) {
      return {
        code: 1,
        comment: 'Document marked as uploaded.',
        payload: {} as any
      };
    } else {
      return handleError<void>(response.status, 'Error marking document as uploaded');
    }

  } catch (error) {
    if (axios.isAxiosError(error)) {
      return handleError<void>(-101, error.response?.data?.detail ?? error.message);
    }
    return handleError<void>(-102, 'Unknown error');
  }
};